import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import {
  createStyles,
  makeStyles,
  Theme,
  IconButton,
  Popper,
  MenuList,
} from '@material-ui/core';

import { EllipsisVIcon } from '../icon/solid';
import { useDropdown } from '../../hooks';

type StyleProps = {
  zIndex: number;
}

const useStyles = makeStyles(({ palette, colors }: Theme) => createStyles({
  root: {
    display: 'inline-block',
  },
  arrowTop: {
    position: 'absolute',
    display: 'block',
    width: '2rem',
    height: '1rem',
    top: '0',
    right: '0.5rem',
    '&::after': {
      position: 'absolute',
      display: 'block',
      content: "''",
      borderColor: 'transparent',
      borderBottomColor: '#fff',
      borderWidth: '0 1rem 1rem 1rem',
      borderStyle: 'solid',
      top: '1px',
    },
    '&::before': {
      position: 'absolute',
      display: 'block',
      content: "''",
      borderColor: 'transparent',
      borderBottomColor: colors.border,
      borderWidth: '0 1rem 1rem 1rem',
      borderStyle: 'solid',
      top: '0',
    },
  },
  arrowBottom: {
    position: 'absolute',
    display: 'block',
    width: '2rem',
    height: '1rem',
    bottom: 0,
    right: '0.5rem',
    '&::after': {
      position: 'absolute',
      display: 'block',
      content: "''",
      borderColor: 'transparent',
      borderTopColor: '#FFF',
      borderWidth: '1rem 1rem 0 1rem',
      borderStyle: 'solid',
      top: '0',
    },
    '&::before': {
      position: 'absolute',
      display: 'block',
      content: "''",
      borderColor: 'transparent',
      borderTopColor: colors.border,
      borderWidth: '1rem 1rem 0 1rem',
      borderStyle: 'solid',
      top: '1px',
    },
  },
  menuButton: {
    color: palette.text.primary,
    '&:focus': {
      outline: 'none',
    },
  },
  icon: {
    color: colors.selected,
    width: 16,
    height: 16,
  },
  menuIcon: {
    padding: '3px 0',
    color: palette.icons.default,
  },
  openedMenuIcon: {
    color: palette.menuAction.hover,
  },
  menu: ({zIndex}: StyleProps) =>(
    {
      minWidth: 150,
      padding: '5px',
      zIndex:  zIndex,
    }
  ),
  menuContainerBottom: {
    border: `1px solid ${colors.border}`,
    padding: '5px',
    background: '#fff',
    margin: '0.6rem 0 0 0',
    boxShadow: '1px 1px 10px 1px rgba(0,0,0,0.12)',
  },
  menuContainerTop: {
    border: `1px solid ${colors.border}`,
    padding: '5px',
    background: '#fff',
    margin: '0 0 0.6rem 0',
    boxShadow: '1px 1px 10px 1px rgba(0,0,0,0.12)',
  },

  list: {
    outline: 'none',

    '& li:focus': {
      backgroundColor: 'rgba(0,168,190,0.05)',
    },

  },
}), { name: 'AdvancedActionCell' });

type Props = {
  className?: string;
  children: React.ReactNode | React.ReactNodeArray;
  disabledPortal?: boolean;
  zIndex?: number
};

const stopPropagation: React.MouseEventHandler = (e) => {
  e.stopPropagation();
};

const AdvancedActionCell = ({ children, className, disabledPortal = true, zIndex = 2147483100 }: Props) => {
  const menuRef = useRef<HTMLButtonElement | null>(null);
  if (children && Array.isArray(children)) {
    children = children.filter((item) => item !== false);
    if (Array.isArray(children) && children.length === 0) {
      children = null;
    }
  }

  const classes = useStyles({zIndex});
  const listRef = useRef<HTMLUListElement>(null);
  const {
    isOpened, open, close, toggle,
  } = useDropdown();

  const keyDownHandler = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (isOpened) toggle();
    }
  }, [isOpened, toggle]);

  useEffect(() => {
    if (isOpened && listRef && listRef.current) {

      setTimeout(() => {
        if (listRef && listRef.current) {
          listRef.current.focus();
        }
      }, 50);
    }
  }, [isOpened, listRef]);

  useEffect(() => {
    const scrollCb = () => {
      if(isOpened){
        close();
      }
    }
    window.addEventListener('scroll', scrollCb);

    return () => {
      window.removeEventListener('scroll', scrollCb);
    }
  }, [isOpened, close]);

  if (!children) {
    return null;
  }

  return (
    <div className={clsx(classes.root, className)} onClick={stopPropagation}>
      <IconButton
        tabIndex={-1}
        onMouseEnter={open}
        onMouseLeave={close}
        onClick={toggle}
        className={classes.menuButton}
        ref={menuRef}
      >
        <EllipsisVIcon
          className={clsx(classes.menuIcon, {
            [classes.openedMenuIcon]: isOpened,
          })}
        />
      </IconButton>
      <Popper
        hidden={!isOpened}
        anchorEl={menuRef.current}
        open={isOpened}
        onMouseEnter={open}
        onMouseLeave={close}
        onClick={close}
        placement="bottom-end"
        className={classes.menu}
        keepMounted
        disablePortal={disabledPortal}
        modifiers={{
          flip: {
            enabled: true,
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'window',
          },
        }}
        onKeyDown={keyDownHandler}
      >
        {({ placement }) => (
          <div
            className={(placement.indexOf('bottom') !== -1) ? classes.menuContainerBottom : classes.menuContainerTop}
          >
            <i className={(placement.indexOf('bottom') !== -1) ? classes.arrowTop : classes.arrowBottom} />
            <MenuList classes={{ root: classes.list }} ref={listRef}>
              {children}
            </MenuList>
          </div>
        )}

      </Popper>
    </div>
  );
};

export default AdvancedActionCell;
